import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import SaveButton from "components/buttons/SaveButton";
import rentRequestFields from "config/forms/rentRequestFields";
import CustomTextField from "components/inputs/CustomTextField";
import DropdownField from "components/inputs/DropdownField";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useIsMutating } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import DateField from "components/inputs/DateField";
import CancelButton from "components/buttons/CancelButton";
import { useNavigate } from "react-router";
import enums from "config/enums";
import SpinningCircle from "components/spinners/SpinningCircle";
import { useGetAllContracts } from "hooks/queries/useContracts";
import AlfrescoFileUploader from "components/fileUploaders/AlfrescoFileUploader";
import ExitFormModal from "components/modals/ExitFormModal";
import FormActions from "components/containers/FormActions";

const RentsCreateForm = ({ onCreate }) => {
  const navigate = useNavigate();
  const isCreatingLoading =
    useIsMutating({ mutationKey: ["createRent"] }) +
      useIsMutating({ mutationKey: ["uploadFiles"] }) +
      useIsMutating({ mutationKey: ["deleteMultipleFiles"] }) +
      useIsMutating({ mutationKey: ["deleteFile"] }) >
    0;
  const {
    data: contracts,
    isLoading: isContractsLoading,
    isError: isErrorContracts,
    error: errorContracts,
  } = useGetAllContracts({
    serviceType: ["Rent"],
    status: ["Active"],
  });

  const schema = yup.object(
    rentRequestFields.rent.reduce((acc, field) => {
      let fieldSchema;

      if (field.type === "text" || field.type === "textarea") {
        fieldSchema = yup.string();
      } else if (field.type === "select" || field.type === "boolean") {
        if (field.name === "contractId") {
          fieldSchema = yup.object().shape({
            id: yup.string(),
            label: yup.string(),
          });
        } else fieldSchema = yup.string();
      } else if (field.type === "number") {
        fieldSchema = yup
          .number()
          .transform((value) => (isNaN(value) ? undefined : value));
      } else if (field.type === "date") {
        fieldSchema = yup.date().transform((value, originalValue) => {
          return isNaN(Date.parse(originalValue)) ? undefined : value;
        });
      } else if (field.type === "file") {
        const validTypes = field.validTypes || [];
        const maxSize = field.maxSize || 5000000;

        fieldSchema = yup.mixed().test({
          name: "fileValidation",
          exclusive: true,
          message: "Invalid file",
          test: (value) => {
            if (!value || value.length === 0) return true;

            for (const file of value) {
              if (
                !validTypes.includes(file.type) &&
                !validTypes.includes(file.mimeType)
              ) {
                return new yup.ValidationError(
                  "Μη έγκυρος τύπος αρχείου",
                  null,
                  "file",
                );
              }

              if (file.size > maxSize) {
                return new yup.ValidationError(
                  `Το μέγεθος του αρχείου δεν πρέπει να υπερβαίνει τα ${maxSize / 1000000}MB`,
                  null,
                  "file",
                );
              }
            }

            return true;
          },
        });
      }

      if (field.required) {
        fieldSchema = fieldSchema.required("Το πεδίο είναι υποχρεωτικό");
      } else {
        fieldSchema = fieldSchema.nullable();
      }

      if (field.validation === "custom" && field.regex) {
        fieldSchema = fieldSchema.matches(field.regex, field.validationMessage);
      }

      acc[field.name] = fieldSchema.typeError(
        field.validationMessage || "Μη έγκυρη τιμή",
      );

      return acc;
    }, {}),
  );

  const defaultValues = rentRequestFields.rent.reduce((acc, field) => {
    acc[field.name] = field.defaultValue;
    return acc;
  }, {});

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    getValues,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
  });

  if (isContractsLoading) {
    return <SpinningCircle />;
  }

  if (isErrorContracts) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          height: "100%",
        }}
      >
        <p>Error: {errorContracts?.message}</p>
      </Box>
    );
  }

  const getInputElementByFieldType = (field) => {
    if (
      field.type === "text" ||
      field.type === "number" ||
      field.type === "textarea"
    ) {
      return <CustomTextField {...field} control={control} />;
    } else if (field.type === "select" || field.type === "boolean") {
      let availableOptions =
        field.type === "boolean" ? ["Ναι", "Όχι"] : field.options;

      if (field.name === "rentType") {
        availableOptions = enums.RentTypes.map((option) => option.label);
      }
      if (field.name === "contractId") {
        availableOptions = contracts?.items?.map((option) => ({
          id: option.id,
          label: option.name,
        }));
      }
      return (
        <DropdownField
          {...field}
          control={control}
          options={availableOptions}
          boolean={field.type === "boolean"}
        />
      );
    } else if (field.type === "date") {
      return <DateField {...field} control={control} />;
    } else if (field.type === "file") {
      return <AlfrescoFileUploader {...field} control={control} />;
    }
  };

  const formFields = rentRequestFields.rent;

  return (
    <>
      <form onSubmit={handleSubmit(() => onCreate(getValues()))}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Grid container spacing={2}>
            {formFields
              .filter((field) => field.display !== false)
              .map((field) => {
                if (field.type === "file") {
                  return (
                    <Grid item xs={12} key={field?.name}>
                      <Box width={{ xs: "100%", lg: "50%" }}>
                        {getInputElementByFieldType(field)}
                      </Box>
                    </Grid>
                  );
                } else
                  return (
                    <Grid item xs={12} md={4} key={field?.name}>
                      {getInputElementByFieldType(field)}
                    </Grid>
                  );
              })}
          </Grid>

          {Object.keys(errors).length > 0 && (
            <Typography color="error" fontSize={14}>
              Παρακαλούμε ελέγξτε ότι όλα τα πεδία έχουν συμπληρωθεί σωστά
            </Typography>
          )}

          <FormActions
            startActions={
              <CancelButton
                onClick={() => {
                  reset();
                  navigate(-1);
                }}
              />
            }
            endActions={
              <SaveButton text="Δημιουργία" isLoading={isCreatingLoading} />
            }
          />
        </Box>
      </form>

      <ExitFormModal isFormDirty={isDirty} />
    </>
  );
};

RentsCreateForm.propTypes = {
  onCreate: PropTypes.func,
};

export default RentsCreateForm;
